import type {
  ArcadeChallenge,
  ChallengeAttemptResult,
  ChallengeRules,
  ChallengeTarget,
  CreateChallengeResponse,
  GuestSession,
  PlayEventName,
  RecordPlayEventsResponse,
  SubmitChallengeAttemptResponse,
  ErrorCode,
} from '@knockonwood/arcade-card-shared';
import { ArcadeCardError } from './errors.js';

export interface ArcadePlayConfig {
  gameId: string;
  apiBase?: string;
  /** Override persistence in tests or privacy-restricted browser contexts. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

interface StoredGuest extends GuestSession {
  expiresAt: number;
}

/**
 * Account-optional studio play services. A guest token identifies a browser,
 * not a person: no email, display name, wallet, or purchased balance required.
 */
export class ArcadePlay {
  private gameId = '';
  private apiBase = '';
  private storage: ArcadePlayConfig['storage'] = null;
  private guest: StoredGuest | null = null;
  private sessionId = randomId();

  async init(config: ArcadePlayConfig): Promise<void> {
    this.gameId = config.gameId;
    this.apiBase = (config.apiBase ?? globalThis.location?.origin ?? '').replace(/\/$/, '');
    this.storage = config.storage === undefined ? globalThis.localStorage ?? null : config.storage;
    const saved = this.readGuest();
    if (saved && saved.expiresAt > Date.now() + 60_000) this.guest = saved;
    else await this.createGuest();
  }

  get guestId(): string | null {
    return this.guest?.guestId ?? null;
  }

  async createChallenge(input: {
    seed: string;
    rules: ChallengeRules;
    target: ChallengeTarget;
  }): Promise<ArcadeChallenge> {
    const response = await this.authed<CreateChallengeResponse>('/v1/play/challenges', {
      method: 'POST',
      body: JSON.stringify({ gameId: this.gameId, ...input }),
    });
    return response.challenge;
  }

  async getChallenge(id: string): Promise<ArcadeChallenge> {
    const response = await this.request<CreateChallengeResponse>(
      `/v1/play/challenges/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    return response.challenge;
  }

  async submitAttempt(
    challengeId: string,
    result: ChallengeAttemptResult,
  ): Promise<SubmitChallengeAttemptResponse> {
    return this.authed(`/v1/play/challenges/${encodeURIComponent(challengeId)}/attempts`, {
      method: 'POST',
      body: JSON.stringify({ result }),
    });
  }

  async track(
    name: PlayEventName,
    options: {
      challengeId?: string;
      properties?: Record<string, string | number | boolean | null>;
    } = {},
  ): Promise<number> {
    const response = await this.authed<RecordPlayEventsResponse>('/v1/play/events', {
      method: 'POST',
      body: JSON.stringify({ events: [{
        name,
        gameId: this.gameId,
        sessionId: this.sessionId,
        challengeId: options.challengeId,
        occurredAt: new Date().toISOString(),
        properties: options.properties,
      }] }),
    });
    return response.accepted;
  }

  private async createGuest(): Promise<void> {
    const session = await this.request<GuestSession>('/v1/play/guest', { method: 'POST' });
    this.guest = { ...session, expiresAt: Date.now() + session.expiresIn * 1000 };
    this.storage?.setItem(this.storageKey(), JSON.stringify(this.guest));
  }

  private async authed<T>(path: string, init: RequestInit): Promise<T> {
    if (!this.guest) throw new ArcadeCardError('NOT_INITIALIZED', 'Call init() before using play services');
    const withToken = (): RequestInit => ({
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.guest?.token}` },
    });
    try {
      return await this.request<T>(path, withToken());
    } catch (error) {
      if (!(error instanceof ArcadeCardError) || !['UNAUTHORIZED', 'TOKEN_EXPIRED'].includes(error.code)) throw error;
      this.storage?.removeItem(this.storageKey());
      await this.createGuest();
      return this.request<T>(path, withToken());
    }
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.apiBase}${path}`, init);
    } catch (error) {
      throw new ArcadeCardError('NETWORK', (error as Error).message || 'Network error');
    }
    if (!response.ok) {
      const body = await response.json().catch(() => null) as { code?: string; message?: string } | null;
      throw new ArcadeCardError((body?.code ?? 'INTERNAL') as ErrorCode, body?.message ?? `HTTP ${response.status}`, response.status);
    }
    return response.json() as Promise<T>;
  }

  private storageKey(): string {
    return `kow.arcade-play.${this.gameId}.v1`;
  }

  private readGuest(): StoredGuest | null {
    try {
      const raw = this.storage?.getItem(this.storageKey());
      return raw ? JSON.parse(raw) as StoredGuest : null;
    } catch {
      return null;
    }
  }
}

function randomId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
